import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
  Tooltip,
  Switch,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth, PERMISSIONS } from '../context/AuthContext';
import { LoadingSpinner, ConfirmDialog } from '../components/common';
import { useApp } from '../context/AppContext';

// Permission groups for display
const PERMISSION_GROUPS = {
  Dashboard: ['dashboard:view'],
  Leads: ['leads:view', 'leads:create', 'leads:edit', 'leads:delete'],
  Clients: ['clients:view', 'clients:create', 'clients:edit', 'clients:delete'],
  Contacts: ['contacts:view', 'contacts:edit', 'contacts:delete'],
  Billing: ['billing:view', 'billing:create', 'billing:edit', 'billing:delete'],
  Analytics: ['analytics:view'],
  'DM Templates': ['templates:view', 'templates:create', 'templates:edit', 'templates:delete'],
  Settings: ['settings:view', 'settings:edit'],
  'API Keys': ['api-keys:view', 'api-keys:create', 'api-keys:edit', 'api-keys:delete'],
  Users: ['users:view', 'users:create', 'users:edit', 'users:delete'],
};

const getPermissionLabel = (permission) => {
  const parts = permission.split(':');
  return parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
};

const Users = () => {
  const { api, user: currentUser, hasPermission } = useAuth();
  const { showSnackbar } = useApp();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    permissions: [],
    isActive: true,
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      showSnackbar('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        permissions: user.permissions || [],
        isActive: user.isActive,
      });
    } else {
      setSelectedUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        permissions: [
          PERMISSIONS.DASHBOARD_VIEW,
          PERMISSIONS.LEADS_VIEW,
          PERMISSIONS.LEADS_CREATE,
          PERMISSIONS.LEADS_EDIT,
        ],
        isActive: true,
      });
    }
    setFormError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormError('');
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handlePermissionChange = (permission) => {
    const newPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter((p) => p !== permission)
      : [...formData.permissions, permission];
    setFormData({ ...formData, permissions: newPermissions });
  };

  const handleGroupToggle = (groupPermissions) => {
    const allSelected = groupPermissions.every((p) =>
      formData.permissions.includes(p)
    );
    const newPermissions = allSelected
      ? formData.permissions.filter((p) => !groupPermissions.includes(p))
      : [...new Set([...formData.permissions, ...groupPermissions])];
    setFormData({ ...formData, permissions: newPermissions });
  };

  const handleSubmit = async () => {
    try {
      setFormError('');

      if (!formData.name || !formData.email) {
        setFormError('Name and email are required');
        return;
      }

      if (!selectedUser && !formData.password) {
        setFormError('Password is required for new users');
        return;
      }

      if (formData.password && formData.password.length < 6) {
        setFormError('Password must be at least 6 characters');
        return;
      }

      const payload = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        permissions: formData.role === 'admin' ? [] : formData.permissions,
        isActive: formData.isActive,
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      if (selectedUser) {
        await api.put(`/users/${selectedUser._id}`, payload);
        showSnackbar('User updated successfully', 'success');
      } else {
        await api.post('/users', payload);
        showSnackbar('User created successfully', 'success');
      }

      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      setFormError(error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${selectedUser._id}`);
      showSnackbar('User deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to delete user', 'error');
    }
  };

  const handleResetPassword = async () => {
    try {
      if (!newPassword || newPassword.length < 6) {
        showSnackbar('Password must be at least 6 characters', 'error');
        return;
      }

      await api.put(`/users/${selectedUser._id}/reset-password`, {
        newPassword,
      });
      showSnackbar('Password reset successfully', 'success');
      setResetPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to reset password', 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading users..." />;
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            User Management
          </Typography>
          {hasPermission(PERMISSIONS.USERS_CREATE) && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add User
            </Button>
          )}
        </Box>
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.role === 'admin' ? (
                        <AdminIcon color="primary" sx={{ fontSize: 20 }} />
                      ) : (
                        <PersonIcon color="action" sx={{ fontSize: 20 }} />
                      )}
                      <Typography variant="body2" fontWeight={500}>
                        {user.name}
                      </Typography>
                      {user._id === currentUser?.id && (
                        <Chip label="You" size="small" color="primary" sx={{ height: 20 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.role === 'admin' ? 'Admin' : 'User'}
                      size="small"
                      color={user.role === 'admin' ? 'primary' : 'default'}
                      sx={{ height: 22 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={user.isActive ? 'success' : 'error'}
                      sx={{ height: 22 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {user.role === 'admin'
                        ? 'All permissions'
                        : `${user.permissions?.length || 0} permissions`}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      {hasPermission(PERMISSIONS.USERS_EDIT) && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedUser(user);
                                setResetPasswordDialogOpen(true);
                              }}
                            >
                              <LockIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {hasPermission(PERMISSIONS.USERS_DELETE) &&
                        user._id !== currentUser?.id && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedUser(user);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Full Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            sx={{ mt: 1, mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          />

          {!selectedUser && (
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
              helperText="At least 6 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              name="role"
              value={formData.role}
              onChange={handleChange}
              label="Role"
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin (Full Access)</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
            }
            label="Active"
            sx={{ mb: 2 }}
          />

          {formData.role !== 'admin' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Permissions
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Select which features this user can access
              </Typography>

              {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => (
                <Box key={group} sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permissions.every((p) =>
                          formData.permissions.includes(p)
                        )}
                        indeterminate={
                          permissions.some((p) => formData.permissions.includes(p)) &&
                          !permissions.every((p) => formData.permissions.includes(p))
                        }
                        onChange={() => handleGroupToggle(permissions)}
                      />
                    }
                    label={<Typography variant="body2" fontWeight={600}>{group}</Typography>}
                  />
                  <FormGroup sx={{ ml: 3 }}>
                    {permissions.map((permission) => (
                      <FormControlLabel
                        key={permission}
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.permissions.includes(permission)}
                            onChange={() => handlePermissionChange(permission)}
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {getPermissionLabel(permission)}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete User"
        message={`Are you sure you want to delete "${selectedUser?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedUser(null);
        }}
      />

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => {
          setResetPasswordDialogOpen(false);
          setSelectedUser(null);
          setNewPassword('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for {selectedUser?.name}
          </Typography>
          <TextField
            fullWidth
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="At least 6 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResetPasswordDialogOpen(false);
              setSelectedUser(null);
              setNewPassword('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleResetPassword}>
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
